import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import {
    CableAction,
    CableActionEnum,
    SynopticPlanItem,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { SideTubeEditComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-edit/side-cables-edit/side-tube-edit/side-tube-edit.component';
import {
    CableDirectionEnum,
    PlanTypeEnum,
    SynopticBoxPlanCable,
    SynopticBoxPlanFiber,
} from '@connect-services/client/sdk/api';
import { SynopticBayPlanEditService } from '@connect-services/client/services/synoptic/edit/synoptic-bay-plan-edit.service';
import { SynopticPlanEditService } from '@connect-services/client/services/synoptic/edit/synoptic-plan-edit.service';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule, SideTubeEditComponent],
    selector: 'app-side-cables-edit',
    templateUrl: './side-cables-edit.component.html',
})
export class SideCablesEditComponent {
    public readonly cable = input.required<SynopticBoxPlanCable>();
    public readonly cableOrderChange = output<CableAction>();
    public readonly cassettes = input.required<SynopticPlanItem[][]>();
    public readonly index = input.required<number>();
    public readonly isLast = input.required<boolean>();
    public readonly openedCassettes = input.required<Array<[string, SynopticPlanItem[]]>>();
    public readonly planType = input.required<PlanTypeEnum>();
    public readonly remove = output<void>();
    public readonly side = input.required<CableDirectionEnum>();
    public readonly toggleTube = output<void>();

    protected readonly isCableOpen = computed<boolean>(() => {
        return this.openedCassettes().some((value: [string, SynopticPlanItem[]]) => {
            return value[1].some(
                (cassetteItem: SynopticPlanItem) =>
                    cassetteItem.cable_entrant === this.cable().code ||
                    cassetteItem.cable_sortant === this.cable().code,
            );
        });
    });
    protected readonly tubes = computed<Map<number, SynopticBoxPlanFiber[]>>(() => {
        const entries: Array<[number, SynopticBoxPlanFiber[]]> = Object.entries(this.cable().tubes).map(
            ([tubeKey, fibers]: [string, SynopticBoxPlanFiber[]]) => [Number(tubeKey), fibers],
        );

        return new Map(
            entries.sort((a: [number, SynopticBoxPlanFiber[]], b: [number, SynopticBoxPlanFiber[]]) => a[0] - b[0]),
        );
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly CableActionEnum = CableActionEnum;
    protected readonly CableDirectionEnum = CableDirectionEnum;
    protected readonly synopticPlanEditService = inject(SynopticPlanEditService);

    private readonly synopticBayPlanEditService = inject(SynopticBayPlanEditService);

    protected areAllFibersAssigned(tubeIndex: number): boolean {
        let cassettes = this.cassettes();
        if (this.planType() === PlanTypeEnum.BAY) {
            // For bay plan, we check assignment of cassettes of all drawers, not onl the current one
            cassettes = this.synopticBayPlanEditService.cassettes();
        }

        const fibers = this.tubes().get(tubeIndex) ?? [];
        const positionsAssigned = cassettes.reduce((acc: SynopticPlanItem[], cassette: SynopticPlanItem[]) => {
            const itemsLinked = cassette.filter(this.getCassetteTubePredicate(tubeIndex));

            return [...acc, ...itemsLinked];
        }, []);

        return fibers.length === positionsAssigned.length;
    }

    protected isTubeLinkedToOpenedCassette(tubeIndex: number): boolean {
        return this.openedCassettes().some((value: [string, SynopticPlanItem[]]) => {
            return value[1].some(this.getCassetteTubePredicate(tubeIndex));
        });
    }

    protected toggleCableSelection(): void {
        this.synopticPlanEditService.selectedCables.update((cableCodes: Set<string>) => {
            const code = this.cable().code;
            const updated = new Set(cableCodes);
            updated.has(code) ? updated.delete(code) : updated.add(code);

            return updated;
        });
    }

    private getCassetteTubePredicate(tubeIndex: number): (cassetteItem: SynopticPlanItem) => boolean {
        return (cassetteItem: SynopticPlanItem) => {
            const isEnterTube =
                cassetteItem.cable_entrant === this.cable().code &&
                cassetteItem.numero_tube_entrant === tubeIndex.toString();
            const isExitTube =
                cassetteItem.cable_sortant === this.cable().code &&
                cassetteItem.numero_tube_sortant === tubeIndex.toString();

            return isEnterTube || isExitTube;
        };
    }
}
